import { DestroyRef, ElementRef, inject, Injectable } from '@angular/core';
import { MibBreakpointService } from '@medinbox/angular';
import { CARDS_MOTION, CARDS_REFLOW } from '@shared/constants';
import { CardPlace } from '@shared/models';
import { easeAt, staggerOf } from '@shared/utils';

const FILTERS_DROP_FADE_MS = 300;

function placeOf(card: Element, grid: DOMRect): CardPlace {
  const { left, top, width } = card.getBoundingClientRect();
  return { x: left - grid.left, y: top - grid.top, width };
}

/** The element waits its turn at its former place, then slides to its cell; a card takes its new width on the way. */
function slideCard(
  element: Element,
  from: CardPlace,
  to: CardPlace,
  delay: number,
  duration: number = CARDS_MOTION.DURATION,
): Animation | undefined {
  const [dx, dy] = [from.x - to.x, from.y - to.y];
  if (!dx && !dy && from.width === to.width) {
    return undefined;
  }
  const start: Keyframe = { translate: `${dx}px ${dy}px` };
  const end: Keyframe = { translate: '0 0' };
  if (element.tagName === 'MIB-CASE-CARD') {
    start['width'] = `${from.width}px`;
    end['width'] = `${to.width}px`;
  }
  return element.animate([start, end], {
    duration,
    delay,
    easing: CARDS_MOTION.EASE,
    fill: 'backwards',
  });
}

/** What a reflow is for: the chat or the filters pushing the cases, or a sort reordering them. */
type CardsMove = 'push' | 'reorder';

interface Shot {
  element: HTMLElement;
  rect: DOMRect;
}

/** `rowFavourites`: the favourites are the one-row strip, so they come and go by its right `edge`. */
interface Reflow {
  move: CardsMove;
  rowFavourites: boolean;
  edge: number;
}

/** Cards, the favourites tile and the Recent title: what a reflow follows, by `<group>:<id>`. */
const MOTION_KEY = 'data-motion-key';

function keyedIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(`[${MOTION_KEY}]`));
}

function keyOf(element: HTMLElement): string {
  return element.getAttribute(MOTION_KEY) ?? '';
}

function placeAt(rect: DOMRect): CardPlace {
  return { x: rect.left, y: rect.top, width: rect.width };
}

/** On screen in the scroll box, or anywhere without one. */
function seen(view: DOMRect | undefined, rect: DOMRect): boolean {
  return !view || (rect.bottom > view.top && rect.top < view.bottom);
}

/** A push staggers the Recent cards lightly; a sort cascades every section in its new order. */
function keptDelay(move: CardsMove, group: string, index: number): number {
  const { PUSH_STEP, PUSH_STAGGERED, SORT_STEP, SORT_STAGGERED } = CARDS_REFLOW;
  if (move === 'reorder') {
    return Math.min(index, SORT_STAGGERED) * SORT_STEP;
  }
  return group === 'case' ? Math.min(index, PUSH_STAGGERED) * PUSH_STEP : 0;
}

function fadeIn(element: HTMLElement, duration: number, delay: number): void {
  element.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration,
    delay,
    easing: 'ease-out',
    fill: 'backwards',
  });
}

/** A removed element shown again where it was, out of the flow and untouchable, until `animation` ends. */
function leaveFrom(root: HTMLElement, shot: Shot, animate: (copy: HTMLElement) => Animation): void {
  const box = root.getBoundingClientRect();
  const { element, rect } = shot;
  element.inert = true;
  // Unkeyed, so a shot taken while it leaves does not take the copy for a card.
  element.removeAttribute(MOTION_KEY);
  Object.assign(element.style, {
    position: 'absolute',
    left: `${rect.left - box.left}px`,
    top: `${rect.top - box.top}px`,
    width: `${rect.width}px`,
    margin: '0',
    pointerEvents: 'none',
  });
  root.append(element);
  const animation = animate(element);
  animation.onfinish = animation.oncancel = () => element.remove();
}

/**
 * The case list's cards on the move in the page: sliding after the toggle, leaving the grid, and
 * the scroll that keeps the toggle where it was clicked. Provided by the case list.
 */
@Injectable()
export class CardsMotion {
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly reducedMotion = inject(MibBreakpointService).prefersReducedMotion;
  private readonly leavingCards = new WeakSet<Element>();
  private readonly pendingFrames = new Set<number>();
  /** Ends the "Show less" scroll under way, if any. */
  private stopScroll = (): void => undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.stopScroll();
      this.pendingFrames.forEach((frame) => cancelAnimationFrame(frame));
    });
  }

  /** The tile, then the Recent title and each of its cards: the cards follow in turn, as on a resize. */
  followersOf(button: HTMLElement, recent: HTMLElement | undefined): HTMLElement[] {
    const tile = button.parentElement;
    if (this.reducedMotion() || !tile) {
      return [];
    }
    const title = recent?.querySelector('h2');
    const cards = Array.from(recent?.getElementsByTagName('mib-case-card') ?? []) as HTMLElement[];
    return [tile, ...(title ? [title] : []), ...cards];
  }

  placesOf(followers: HTMLElement[]): CardPlace[] {
    const viewport = new DOMRect();
    return followers.map((follower) => placeOf(follower, viewport));
  }

  /** Slides each follower from its place `before`, the cards in turn. */
  slide(followers: HTMLElement[], before: CardPlace[], besideChat: boolean): void {
    const viewport = new DOMRect();
    const firstCard = followers.findIndex((follower) => follower.tagName === 'MIB-CASE-CARD');
    followers.forEach((follower, i) => {
      const delay = firstCard < 0 || i < firstCard ? 0 : staggerOf(i - firstCard, besideChat);
      slideCard(follower, before[i], placeOf(follower, viewport), delay);
    });
  }

  /**
   * Takes where every keyed element of `root` is; the function returned moves them from there to
   * where they are when it runs: kept ones travel, new ones arrive, removed ones leave.
   */
  shoot(root: HTMLElement): (move: CardsMove, rowFavourites: boolean) => void {
    const before = new Map(
      keyedIn(root).map((element) => [
        keyOf(element),
        { element, rect: element.getBoundingClientRect() },
      ]),
    );
    const right = root.getBoundingClientRect().right;
    return (move, rowFavourites) => {
      // The narrower of the two widths is the strip's edge: past it the box clips.
      const edge = Math.min(right, root.getBoundingClientRect().right) + CARDS_REFLOW.TRAIL_GAP;
      const kept = this.arrive(root, before, { move, rowFavourites, edge });
      this.leave(root, before, kept, { move, rowFavourites, edge });
    };
  }

  /** The folded first row lands where the toggle beside it was clicked. */
  keepToggleInView(button: HTMLElement, top: number, grid: HTMLElement | undefined): void {
    if (!grid) {
      button.scrollIntoView({ block: 'nearest' });
      return;
    }
    this.scrollUp(top - grid.getBoundingClientRect().top);
  }

  /** Scrolls the box up by `rise` on the tile's curve; a wheel or a touch ends it. */
  scrollUp(rise: number): void {
    const box = this.scrollBox();
    if (!box || rise <= 0) {
      return;
    }
    this.stopScroll();
    const from = box.scrollTop;
    const to = Math.max(0, from - rise);
    if (this.reducedMotion()) {
      box.scrollTop = to;
      return;
    }
    const byHand = new AbortController();
    let start: number | undefined;
    const step = (now: number) => {
      start ??= now;
      const t = Math.min(1, (now - start) / CARDS_MOTION.DURATION);
      box.scrollTop = from + (to - from) * easeAt(t);
      if (t < 1) {
        frame = requestAnimationFrame(step);
      } else {
        byHand.abort();
      }
    };
    let frame = requestAnimationFrame(step);
    this.stopScroll = () => {
      cancelAnimationFrame(frame);
      byHand.abort();
    };
    for (const type of ['wheel', 'touchstart']) {
      box.addEventListener(type, this.stopScroll, { signal: byHand.signal, passive: true });
    }
  }

  scrollUpAtOnce(height: number): void {
    const box = this.scrollBox();
    if (box) {
      box.scrollTop = Math.max(0, box.scrollTop - height);
    }
  }

  /** A frame cancelled when the case list goes. */
  onNextFrame(run: () => void): void {
    const frame = requestAnimationFrame(() => {
      this.pendingFrames.delete(frame);
      run();
    });
    this.pendingFrames.add(frame);
  }

  /** The favourites past the first row, measured all before any moves: each one taken out shifts the next. */
  takeLeavingOutOfFlow(grid: HTMLElement, firstRow: number): void {
    const box = grid.getBoundingClientRect();
    const leaving = this.staying(grid).slice(firstRow);
    const places = leaving.map((card) => placeOf(card, box));
    leaving.forEach((card, i) => this.takeOutOfFlow(card, places[i]));
  }

  /** Sinks and fades, the `last` extra favourite first; the sink is the longer of the two. */
  foldOut(card: HTMLElement, extra: number, last: number): Animation {
    const delay = (last - extra) * CARDS_MOTION.SINK_STAGGER;
    card.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: CARDS_MOTION.SINK_FADE,
      delay,
      easing: 'ease-out',
      fill: 'forwards',
    });
    return card.animate([{ translate: '0 0' }, { translate: `0 ${CARDS_MOTION.RISE_Y}px` }], {
      duration: CARDS_MOTION.DURATION,
      delay,
      easing: CARDS_MOTION.EASE,
    });
  }

  fadeOut(card: HTMLElement): Animation {
    return card.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: FILTERS_DROP_FADE_MS,
      easing: 'ease-in-out',
    });
  }

  /** Every keyed element now: kept ones travel, new ones arrive. Returns the keys shown. */
  private arrive(root: HTMLElement, before: Map<string, Shot>, reflow: Reflow): Set<string> {
    const view = this.scrollBox()?.getBoundingClientRect();
    const order = new Map<string, number>();
    const counts = { trailing: 0, entering: 0 };
    const shown = new Set<string>();
    for (const element of keyedIn(root)) {
      const key = keyOf(element);
      const group = key.split(':')[0];
      const index = order.get(group) ?? 0;
      order.set(group, index + 1);
      shown.add(key);
      const from = before.get(key)?.rect;
      const to = element.getBoundingClientRect();
      if (!from) {
        this.enter(element, to, group, index, counts, reflow);
      } else if (seen(view, from) || seen(view, to)) {
        const delay = keptDelay(reflow.move, group, index);
        slideCard(element, placeAt(from), placeAt(to), delay, CARDS_REFLOW.DURATION);
      }
    }
    return shown;
  }

  /** A favourite new to the strip trails in from past its edge; the tile and the others fade in. */
  private enter(
    element: HTMLElement,
    to: DOMRect,
    group: string,
    index: number,
    counts: { trailing: number; entering: number },
    { move, rowFavourites, edge }: Reflow,
  ): void {
    const { GAP } = CARDS_MOTION;
    const { TRAIL_IN, TRAIL_IN_DELAY, TRAIL_IN_STEP, TILE_IN, TILE_IN_DELAY } = CARDS_REFLOW;
    if (group === 'favourite' && rowFavourites) {
      const n = counts.trailing++;
      const push = move === 'push';
      element.animate(
        [{ translate: `${edge + n * (to.width + GAP) - to.left}px 0` }, { translate: '0 0' }],
        {
          duration: push ? TRAIL_IN : CARDS_REFLOW.DURATION,
          delay: push ? TRAIL_IN_DELAY + n * TRAIL_IN_STEP : keptDelay(move, group, index),
          easing: CARDS_MOTION.EASE,
          fill: 'backwards',
        },
      );
      return;
    }
    if (group === 'tile' && move === 'push') {
      fadeIn(element, TILE_IN, TILE_IN_DELAY);
      return;
    }
    const { ENTER, ENTER_DELAY, ENTER_STAGGERED, SORT_STEP } = CARDS_REFLOW;
    fadeIn(element, ENTER, ENTER_DELAY + Math.min(counts.entering++, ENTER_STAGGERED) * SORT_STEP);
  }

  /** A favourite dropped from the strip slides out past its edge; the tile and the others shrink and fade. */
  private leave(
    root: HTMLElement,
    before: Map<string, Shot>,
    shown: Set<string>,
    reflow: Reflow,
  ): void {
    const view = this.scrollBox()?.getBoundingClientRect();
    let trailing = 0;
    before.forEach((shot, key) => {
      if (shown.has(key) || shot.element.isConnected || !seen(view, shot.rect)) {
        return;
      }
      const group = key.split(':')[0];
      if (group === 'favourite' && reflow.rowFavourites) {
        const dx = reflow.edge + trailing++ * (shot.rect.width + CARDS_MOTION.GAP) - shot.rect.left;
        const duration = reflow.move === 'push' ? CARDS_REFLOW.TRAIL_OUT : CARDS_REFLOW.DURATION;
        leaveFrom(root, shot, (copy) =>
          copy.animate([{ translate: '0 0' }, { translate: `${dx}px 0` }], {
            duration,
            easing: CARDS_MOTION.EASE,
            fill: 'forwards',
          }),
        );
        return;
      }
      const tile = group === 'tile' && reflow.move === 'push';
      const scale = tile ? 1 : CARDS_REFLOW.LEAVE_SCALE;
      leaveFrom(root, shot, (copy) =>
        copy.animate(
          [
            { opacity: 1, scale: 1 },
            { opacity: 0, scale },
          ],
          {
            duration: tile ? CARDS_REFLOW.TILE_OUT : CARDS_REFLOW.LEAVE,
            easing: 'ease-out',
            fill: 'forwards',
          },
        ),
      );
    });
  }

  private scrollBox(): Element | null {
    return this.host.closest('[maiScrollPosition]');
  }

  private staying(grid: HTMLElement): HTMLElement[] {
    return Array.from(grid.getElementsByTagName('mib-case-card')).filter(
      (card): card is HTMLElement => card instanceof HTMLElement && !this.leavingCards.has(card),
    );
  }

  /** A leaving card keeps its last place out of the flow, so the cards and the tile measure the final layout. */
  private takeOutOfFlow(card: HTMLElement, place: CardPlace): void {
    this.leavingCards.add(card);
    card.inert = true;
    Object.assign(card.style, {
      position: 'absolute',
      left: `${place.x}px`,
      top: `${place.y}px`,
      width: `${place.width}px`,
    });
  }
}
